import { css } from "@tento-ui/component-styles";
import type { Style } from "@tento-ui/component-styles";
import type { TokenSet } from "@tento-ui/brand/page-engine.types";

// tsh's look, derived from the measured fx.sh port in
// `tento/tento-brand-js/templates/fx/tokens.ts`.
//
// THE WHOLE TYPE SYSTEM IS ONE SIZE, and that is the borrowed idea. fx.sh sets
// every text role — headings included — at 14px mono, and separates them by
// WEIGHT and INK and by nothing else. It is the opposite of what tsh.lona.so
// does today, which runs a five-step Manrope ramp from 38.9px down to 13.9px.
//
// The argument for it here is stronger than "fx does it". A page whose subject
// is a command line should not make the reader switch alphabets between the
// prose and the thing being described: at one size in one face, the install
// one-liner reads as continuous with the sentence introducing it, instead of
// as a specimen in a box. The current page sets its command in mono inside a
// rounded card and everything around it in a sans display ramp, which is
// exactly that switch, performed four times.
//
// Sizes are CAP HEIGHTS, not font sizes — `--text-10px` renders 14.29px of
// font for 10px of cap. The system trims each face's internal leading out of
// every authored margin, so a gap authored here is ink-to-ink space. That is
// also why every gap in the sections is authored larger than it measures: see
// the arithmetic at each site.
// Exported as a SHEET as well as a token set, because the public landing root
// never applies a preset's `tokenSet`: it takes tokens from the design system
// and the page style it was constructed with, and resolves the preset only to
// pick section renderers. A page that relied on the token set therefore
// rendered its own spacing correctly in Manrope on loaf cream — every
// `--tsh-*` var unset, every fallback taken.
//
// So each section adopts this sheet itself. `:host` then means the section,
// the vars land on it and inherit down through its shadow tree, and the design
// travels with the components instead of with the route that happens to mount
// them.
export const tshVars: Style.Compat = css`
    :host {
      /* The face metrics FIRST, because every size below is a cap height and
         the conversion needs them: font-size = cap / (1 - cap-height -
         baseline). 0.15/0.15 is the mono metric the fx port measured, and it
         is what every authored margin in these sections was computed against.
         Getting these wrong does not fail loudly — it shifts every gap on the
         page by a few pixels and looks like sloppy spacing. */
      --cap-height: 0.15;
      --baseline: 0.15;

      /* One size, every role: 9.5px of cap, which renders 13.57px of font. A
         heading is 13.57/700; body is 13.57/400.

         Written as rem rather than as a var(--text-10px) reference: that
         token belongs to the scale a @tento-brand template carries, and it
         does not exist in so.lona. Referencing it does not warn — the
         declaration becomes
         invalid at computed-value time and the custom property silently reads
         as unset, which is how this page first rendered its whole type system
         at the browser default. */
      --h1-size: 0.59375rem;
      --h2-size: 0.59375rem;
      --h3-size: 0.59375rem;
      --p-size: 0.59375rem;
      --font-size: 0.59375rem;

      /* Secondary chrome — 12.14px, so 8.5px of cap. */
      --small-size: 0.53125rem;

      /* 1.75 is the loosest leading in any of these ports, and a 560px measure
         of mono needs it: mono sets wide, so a line is short in words and long
         in pixels, and a stack of short lines at a tight leading reads as a
         block of code rather than as prose. */
      --line-height-ratio: 1.75;

      /* Tailwind's neutral ramp, unmodified — the same five greys fx uses.
         There is no accent anywhere in the PAGE, which is a decision rather
         than an omission: the only coloured thing on a developer-tool page
         should be the terminal. The terminal palette below is where that
         colour lives, and the page around it stays grey so the figures are
         the only thing on the page competing for the eye. */
      --tsh-bg: #ffffff;
      --tsh-surface: #fafafa;
      --tsh-border: #e5e5e5;
      --tsh-faint: #9e9e9e;
      --tsh-muted: #525252;
      --tsh-fg: #171717;

      /* Prose stays narrow. Single terminals keep the old measure; paired and
         three-pane captures get enough room to read as adjacent terminals. */
      --tsh-measure: 560px;
      --tsh-panel: 680px;
      --tsh-wide-panel: 1100px;
      --tsh-gutter: 32px;

      /* 10px, not fx.sh's own 2px: operator direction on the fx panel, applied
         here for consistency with it. */
      --tsh-radius: 10px;

      /* A platform-native terminal stack, named before the generic so the
         page does not let each browser make an arbitrary mono choice. */
      --tsh-mono: "SFMono-Regular", "Cascadia Mono", "Roboto Mono",
        "Liberation Mono", ui-monospace, monospace;

      /* ─── The terminal ───────────────────────────────────────────────────
         One palette, in one place. These were previously spelled out as
         fallback values inside tsh-session.ts — every var(--tsh-term-fg)
         carried its own copy of the colour as its fallback and none of them
         were ever defined, so the palette existed only as a repeated default
         and could not be changed without editing eight call sites.
         (No backtick quoting in this comment: it sits inside a css tagged
         template, where a backtick ends the stylesheet mid-file.)

         The terminal keeps its own scheme in both page themes. A capture is a
         picture of a program, and the program does not repaint itself when the
         reader's OS flips — so these sit outside the light/dark blocks below,
         deliberately. */
      --tsh-term-bg: #002b36;
      --tsh-term-edge: #0a3a47;
      --tsh-term-fg: #d0d0d0;
      --tsh-term-dim: #8a8a8a;
      --tsh-term-accent: #d77757;
      --tsh-term-gutter: 12px;
      --tsh-term-cols: 80;

      /* Two colours read off a real capture rather than picked: the added and
         removed inks tsh renders a diff row with. The accent above is the same
         #d77757 that capture marks a turn with, which is why the page already
         had it. Contrast on the terminal ground: 5.17 and 5.67. */
      --tsh-term-added: #4ea96f;
      --tsh-term-removed: #f7768e;

      /* A node reference — a session id, a semantic path, a turn ref. The
         capture's #c0caf5, which clears 9.30 on this ground. */
      --tsh-term-node: #c0caf5;

      /* The SGR names bound to those roles.
         session-replay decodes escapes into --session-replay-ansi-* custom
         properties and paints whatever they resolve to, so this is the whole
         join between a transcript's escape codes and this page's design — and
         the reason ansi.ts can author by role without naming a colour. Only
         the names the figures actually use are bound; the rest keep the
         component's xterm defaults. */
      --session-replay-ansi-yellow: var(--tsh-term-accent);
      --session-replay-ansi-cyan: var(--tsh-term-node);
      --session-replay-ansi-green: var(--tsh-term-added);
      --session-replay-ansi-red: var(--tsh-term-removed);
    }

    /* The dark scheme, in the same two selectors the fx port uses: an absent
       attribute follows the OS, so the media query carries the default and the
       attribute carries the override — which is what lets a reader who chose
       light keep it on a dark OS. */
    @media (prefers-color-scheme: dark) {
      :host(:not([data-theme="light"])) {
        --tsh-bg: #000000;
        --tsh-surface: #0a0a0a;
        --tsh-border: #262626;
        --tsh-faint: #666666;
        --tsh-muted: #a1a1a1;
        --tsh-fg: #ededed;
      }
    }

    :host([data-theme="dark"]) {
      --tsh-bg: #000000;
      --tsh-surface: #0a0a0a;
      --tsh-border: #262626;
      --tsh-faint: #666666;
      --tsh-muted: #a1a1a1;
      --tsh-fg: #ededed;
    }
`;

export const tshTokens: TokenSet = [tshVars];
